/** Tab-local recovery: the original operation, target, CAS and candidate stay together. */
import { ApiError, createFeedbackWorkspace, saveFeedbackWorkspace, discardFeedbackWorkspace,
  isCurrentFeedbackWorkspaceScope } from './api';
import { refreshFeedbackWorkspace } from './queryClient';
import type { FeedbackWorkspaceCommand, FeedbackWorkspaceScope,
  FeedbackWorkspaceResult, FeedbackWorkspaceDiscardResult } from './types';

type Outcome = FeedbackWorkspaceResult | FeedbackWorkspaceDiscardResult;
export type FeedbackWorkspaceCommandState =
  | { status: 'ready' | 'sending' | 'unknown' | 'rejected' | 'unavailable' }
  | { status: 'retired'; outcome: 'unknown' }
  | { status: 'accepted'; result: Outcome };

function acknowledgement(value: Outcome, command: FeedbackWorkspaceCommand): Outcome {
  if (command.action === 'discard') {
    if (!value || value.schema_version !== 'feedback-workspace-discard-result.v1' || value.discarded !== true
      || Object.keys(value).sort().join(',') !== 'discarded,schema_version') {
      throw new Error('invalid_workspace_acknowledgement');
    }
    return { ...value };
  }
  const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (!value || value.schema_version !== 'feedback-workspace-result.v1' || !uuid.test(value.workspace_id)
    || !Number.isInteger(value.version) || value.version < 1 || value.version > 2147483647
    || (command.action === 'save' && value.workspace_id.toLowerCase() !== command.workspaceId.toLowerCase())
    || Object.keys(value).sort().join(',') !== 'schema_version,version,workspace_id') {
    throw new Error('invalid_workspace_acknowledgement');
  }
  return { ...value };
}

function errorCode(error: unknown): unknown {
  return error instanceof ApiError && error.detail && typeof error.detail === 'object' && 'code' in error.detail
    ? error.detail.code : null;
}

export function captureFeedbackWorkspaceCommand(scope: FeedbackWorkspaceScope, command: FeedbackWorkspaceCommand,
  recovery: { restoredUnknown?: boolean } = {}) {
  const capturedScope = { ...scope };
  const serialized = JSON.stringify(command);
  const intent = (): FeedbackWorkspaceCommand => JSON.parse(serialized) as FeedbackWorkspaceCommand;
  const current = () => isCurrentFeedbackWorkspaceScope(capturedScope);
  let state: FeedbackWorkspaceCommandState = { status: recovery.restoredUnknown ? 'unknown' : 'ready' };
  let unresolved = recovery.restoredUnknown ?? false;
  let inFlight: Promise<Outcome> | null = null;

  async function run(signal?: AbortSignal): Promise<Outcome> {
    state = { status: 'sending' };
    const original = intent();
    const options = { scope: capturedScope, signal };
    try {
      const value = original.action === 'create' ? await createFeedbackWorkspace(original.body, options)
        : original.action === 'save' ? await saveFeedbackWorkspace(original.workspaceId, original.body, options)
          : await discardFeedbackWorkspace(original.workspaceId, original.body, options);
      const result = acknowledgement(value, original);
      unresolved = false;
      state = { status: 'accepted', result };
      const workspaceId = result.schema_version === 'feedback-workspace-result.v1' ? result.workspace_id
        : original.action === 'discard' ? original.workspaceId : null;
      if (workspaceId) void Promise.resolve()
        .then(() => refreshFeedbackWorkspace(capturedScope, workspaceId, original.action === 'discard')).catch(() => {});
      return { ...result };
    } catch (error) {
      const code = errorCode(error);
      if (code === 'workspace_operation_unresolved' || code === 'workspace_operation_retired'
        || !(error instanceof ApiError && error.status >= 400 && error.status < 500)) unresolved = true;
      // Retirement may follow discard OR erasure. It never acknowledges deletion,
      // even when this caller's previous send had an unknown outcome.
      state = code === 'workspace_operation_retired' ? { status: 'retired', outcome: 'unknown' }
        : { status: unresolved ? 'unknown' : 'rejected' };
      throw error;
    } finally {
      inFlight = null;
    }
  }

  return {
    // A shared continuation may adopt a command dispatched by another controller.
    // Its later 4xx cannot prove that the earlier attempt was never accepted.
    adoptUnresolved(): void {
      if (!current() || state.status === 'accepted') return;
      unresolved = true;
      state = { status: 'unknown' };
    },
    getState(): FeedbackWorkspaceCommandState {
      if (!current()) return { status: 'unavailable' };
      return state.status === 'accepted' ? { ...state, result: { ...state.result } } : { ...state };
    },
    getIntent(): FeedbackWorkspaceCommand | null { return current() ? intent() : null; },
    send(signal?: AbortSignal): Promise<Outcome> {
      if (!current() || signal?.aborted) return Promise.reject(new DOMException('Workspace context changed', 'AbortError'));
      if (inFlight) return inFlight;
      if (state.status === 'accepted') return Promise.resolve({ ...state.result });
      inFlight = run(signal);
      return inFlight;
    },
  };
}
